import { useTheme } from 'styled-components';
import { FormEvent, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  FormErrorTypes,
  Modal,
  ModalContext,
  Text,
  toast,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
} from 'dash-ui';
import { DashValidation } from 'dash-common';

import { BrokerModals } from 'pages/brokers/constants/constants';
import {
  GetBrokerByIdDocument,
  GetBrokersDocument,
  useBlockBrokerMutation,
} from 'gql/generated.graphql';

enum FormFields {
  Reason = 'reason',
}

const FormFieldsList = [FormFields.Reason];

const BlockBrokerModal = () => {
  const { t } = useTranslation(['pages', 'common', 'forms']);
  const translateErrorMessage = useServerResponseTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const theme = useTheme();

  const [formErrors, , [setReasonError]] = useFormValidation(FormFieldsList);
  const [formState, [setReason]] = useFormState(FormFieldsList);

  const { isOpenCurrent, toggleModal, payload, setPayload } =
    useContext(ModalContext);

  const isOpen = isOpenCurrent[BrokerModals.BLOCK_BROKER_MODAL];

  const [blockBroker, { loading }] = useBlockBrokerMutation({
    refetchQueries: [GetBrokersDocument, GetBrokerByIdDocument],
  });

  const validateReason = (value: string) => {
    if (!value) {
      setReasonError({
        type: FormErrorTypes.Invalid,
        message: t`forms:messages:reasonBlank`,
      });
      return false;
    }
    if (value.length > DashValidation.MaxBrokerArchivingReasonLength) {
      setReasonError({
        type: FormErrorTypes.Invalid,
        message: t`forms:messages:fieldLengthInvalid`,
      });
      return false;
    }
    setReasonError(null);
    return true;
  };

  const onReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
    validateReason(e.target.value);
  };

  const closeModal = () => {
    toggleModal(false, BrokerModals.BLOCK_BROKER_MODAL);
    setPayload(null);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    blockBroker({
      variables: {
        brokerId: payload?.brokerId,
        archivingReason: formState.reason,
      },
    })
      .then(() => {
        closeModal();
      })
      .catch((error) => {
        toast.error(translateErrorMessage(error));
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      submitButton={{
        disabled: loading,
        isLoading: loading,
        title: t('common:labels.confirm'),
        colorVariation: 'danger',
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t('common:labels.cancel'),
        onClick: closeModal,
      }}
    >
      <Text bold xl>
        {t('pages:brokersPage.blockBroker')}
      </Text>
      <Text color={theme.colors.n80}>
        {t('pages:brokersPage.modals.blockBrokerSubtitle')}
      </Text>
      <Form ref={formRef} onSubmit={onSubmit} mt={3} w="100%">
        <Form.Group>
          <Form.Label md>
            {t('pages:brokersPage.modals.blockBrokerReasonFieldTitle')}
          </Form.Label>
          <Form.Textarea
            required
            w="100%"
            // FIXME
            h="88px !important"
            placeholder={
              t('pages:brokersPage.modals.blockBrokerReasonFieldPlaceholder') ||
              ''
            }
            value={formState.reason || ''}
            onChange={onReasonChange}
            $errorType={formErrors[FormFields.Reason]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Reason]?.type}>
            {formErrors[FormFields.Reason]?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default BlockBrokerModal;
